<template>
  <div class="raw-json-wrapper">
    <!-- TOGGLE BUTTON -->
    <div class="raw-json-action">
      <button
        type="button"
        class="button is-small is-light json-toggle-btn"
        :class="{ 'is-blue is-outline': isOpen }"
        @click="toggle"
        :title="isOpen ? 'JSON-Rohdaten ausblenden' : 'JSON-Rohdaten für Entwickler und direkte Imports anzeigen'"
      >
        <span class="material-symbols-outlined">code</span>
        {{ isOpen ? buttonLabelHide : buttonLabel }}
      </button>
    </div>

    <!-- EXPANDABLE RAW JSON PANEL -->
    <div v-if="isOpen" class="raw-json-panel">
      <!-- HEADER WITH TITLE AND ACTION BUTTONS -->
      <div class="raw-json-header">
        <div class="raw-json-title-wrap">
          <strong class="raw-json-title">
            <span class="material-symbols-outlined">data_object</span>
            {{ title }}
          </strong>
          <span class="raw-json-subtitle is-size-7 has-text-grey">{{ subtitle }}</span>
        </div>

        <div class="buttons are-small mb-0">
          <button
            type="button"
            class="button is-small is-light"
            @click="copyToClipboard"
            title="In Zwischenablage kopieren"
          >
            <span class="material-symbols-outlined">content_copy</span>
            {{ copySuccess ? 'Kopiert!' : copyButtonText }}
          </button>
          <button
            type="button"
            class="button is-small is-blue is-outline"
            @click="apply"
            title="Aktuelles JSON validieren und ins Formular übernehmen"
          >
            <span class="material-symbols-outlined">sync</span>
            {{ applyButtonText }}
          </button>
        </div>
      </div>

      <!-- JSON TEXTAREA -->
      <textarea
        v-model="rawJsonInput"
        class="textarea is-small json-textarea"
        :rows="rows"
        spellcheck="false"
        :placeholder="placeholder"
        @input="onUserType"
      ></textarea>

      <!-- ERROR MESSAGE (ICON + TEXT VERTICALLY CENTERED) -->
      <div v-if="error" class="notification is-danger is-light raw-json-message">
        <span class="material-symbols-outlined message-icon">error</span>
        <span class="message-text">{{ error }}</span>
      </div>

      <!-- SUCCESS MESSAGE (ICON + TEXT VERTICALLY CENTERED) -->
      <div v-if="success" class="notification is-success is-light raw-json-message">
        <span class="material-symbols-outlined message-icon">check_circle</span>
        <span class="message-text">{{ successMessage }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PharmacyRawJsonPanel',

  props: {
    // Current data to serialize (Object, Array, or String)
    jsonData: {
      type: [Object, Array, String],
      default: () => ({})
    },
    // Header title text, e.g. "JSON-Rohdaten (Eigenmarken-Kategorien)"
    title: {
      type: String,
      default: 'JSON-Rohdaten'
    },
    // Subtitle badge text next to title
    subtitle: {
      type: String,
      default: 'Server-Payload'
    },
    // Toggle button label when closed
    buttonLabel: {
      type: String,
      default: 'JSON'
    },
    // Toggle button label when open
    buttonLabelHide: {
      type: String,
      default: 'JSON-Rohdaten ausblenden'
    },
    // Number of textarea rows
    rows: {
      type: Number,
      default: 8
    },
    // Textarea placeholder
    placeholder: {
      type: String,
      default: ''
    },
    // Text on the apply button
    applyButtonText: {
      type: String,
      default: 'Aus JSON übernehmen'
    },
    // Text on the copy button
    copyButtonText: {
      type: String,
      default: 'Kopieren'
    },
    // Text displayed upon successful apply
    successMessage: {
      type: String,
      default: 'JSON erfolgreich geladen und übernommen!'
    }
  },

  data() {
    return {
      isOpen: false,
      rawJsonInput: '',
      copySuccess: false,
      error: '',
      success: false,
      isUserEditing: false
    };
  },

  watch: {
    jsonData: {
      deep: true,
      handler(newVal) {
        // If panel is open and user isn't actively editing, keep formatted display in sync
        if (this.isOpen && !this.isUserEditing) {
          this.syncFromProp();
        }
      }
    }
  },

  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.syncFromProp();
        this.error = '';
        this.success = false;
        this.isUserEditing = false;
      }
      this.$emit('toggle', this.isOpen);
    },

    syncFromProp() {
      if (typeof this.jsonData === 'string') {
        try {
          const parsed = JSON.parse(this.jsonData);
          this.rawJsonInput = JSON.stringify(parsed, null, 2);
        } catch (e) {
          this.rawJsonInput = this.jsonData;
        }
      } else {
        this.rawJsonInput = JSON.stringify(this.jsonData, null, 2);
      }
    },

    onUserType() {
      this.isUserEditing = true;
      if (this.error) {
        this.error = '';
      }
    },

    copyToClipboard() {
      if (!this.rawJsonInput) return;
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.rawJsonInput).then(() => {
          this.copySuccess = true;
          setTimeout(() => {
            this.copySuccess = false;
          }, 2000);
        }).catch(() => {
          this.fallbackCopy();
        });
      } else {
        this.fallbackCopy();
      }
    },

    fallbackCopy() {
      try {
        const temp = document.createElement('textarea');
        temp.value = this.rawJsonInput;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);
        this.copySuccess = true;
        setTimeout(() => {
          this.copySuccess = false;
        }, 2000);
      } catch (err) {
        console.error('Copy failed:', err);
      }
    },

    apply() {
      this.error = '';
      this.success = false;
      let parsed;

      try {
        parsed = JSON.parse(this.rawJsonInput);
      } catch (e) {
        this.error = 'Ungültiges JSON-Format: ' + e.message;
        return;
      }

      // Provide callback to parent handler
      const done = (err) => {
        if (err) {
          this.error = typeof err === 'string' ? err : 'Fehler beim Übernehmen des JSON.';
          this.success = false;
        } else {
          this.error = '';
          this.success = true;
          this.isUserEditing = false;
          setTimeout(() => {
            this.success = false;
          }, 3000);
        }
      };

      this.$emit('apply', parsed, done);
    },

    // Public helper methods
    setError(message) {
      this.error = message;
      this.success = false;
    },

    setSuccess(message) {
      this.error = '';
      this.success = true;
      setTimeout(() => {
        this.success = false;
      }, 3000);
    }
  }
};
</script>

<style lang="scss" scoped>
.raw-json-wrapper {
  width: 100%;
}

/* TOGGLE BUTTON ACTION */
.raw-json-action {
  display: flex;
  justify-content: flex-start;
  margin-top: 10px;

  .json-toggle-btn {
    height: 28px;
    font-size: 12px;
    color: #666;
    border-radius: 4px;

    .material-symbols-outlined {
      font-size: 16px;
      margin-right: 4px;
    }

    &:hover {
      color: #2780e3;
    }
  }
}

/* EXPANDABLE RAW JSON PANEL */
.raw-json-panel {
  background-color: #2b2b2b;
  color: #f1f1f1;
  border-radius: 4px;
  padding: 12px;
  margin-top: 10px;
  margin-bottom: 10px;
  width: 100%;
  max-width: 100%;

  .raw-json-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;

    .raw-json-title-wrap {
      display: inline-flex;
      align-items: center;
      gap: 14px;
    }

    .raw-json-title,
    strong {
      color: #ffffff !important;
      font-size: 13px !important;
      font-weight: 700 !important;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      line-height: 1;
    }

    .raw-json-subtitle,
    .has-text-grey {
      color: #94a3b8 !important;
      font-size: 11px !important;
      margin-left: 14px !important;
      display: inline-block !important;
      line-height: 1;
    }

    .material-symbols-outlined {
      font-size: 16px;
      vertical-align: middle;
      color: #64b5f6 !important;
    }

    .buttons .button {
      margin-left: 6px;
      .material-symbols-outlined {
        font-size: 14px;
        margin-right: 3px;
      }
    }
  }

  .json-textarea {
    background-color: #1e1e1e;
    color: #a9dc76;
    font-family: 'Consolas', 'Courier New', monospace;
    font-size: 11px;
    line-height: 1.4;
    border: 1px solid #444;
    border-radius: 3px;
    width: 100%;
  }

  /* NOTIFICATION MESSAGES (SUCCESS / ERROR) WITH CENTERING */
  .raw-json-message {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    margin-top: 8px !important;
    margin-bottom: 0 !important;
    padding: 8px 12px !important;
    border-radius: 4px !important;
    font-size: 12px !important;
    line-height: 1.3 !important;

    .message-icon {
      font-size: 18px !important;
      line-height: 1 !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      margin: 0 !important;
      padding: 0 !important;
      flex-shrink: 0 !important;
    }

    .message-text {
      display: inline-flex !important;
      align-items: center !important;
      line-height: 1.3 !important;
      margin: 0 !important;
      padding: 0 !important;
    }
  }
}
</style>

